import {
  Button,
  Input,
  InputCountry,
  InputPercentage,
  InputSelect,
  InputWrapper,
} from '@vrplatform/components';
import {
  useApiMutation,
  useInfiniteQuery,
  useInvalidateHasuraQueries,
  useQuery,
  useTeam,
} from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import type { SelectItem } from '@vrplatform/components';
import {
  showErrorNotification,
  showSuccessNotification,
} from '@vrplatform/theme';
import { bankersRound, toTitleCase } from '@vrplatform/utils';
import { Box, Group, Modal, Tooltip } from '@mantine/core';
import { Text } from '@mantine/core';
import {
  type account_bool_exp,
  type activeStatus_enum,
  defaultAccountOrderBy,
  type taxBehavior_enum,
} from '@vrplatform/graphql';
import { formatAssignmentType, whereAccounts } from '@vrplatform/ui-common';
import { useState } from 'react';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { useTaxRateAccountAssignmentQuery } from './useTaxRateAccountAssignmentQuery';

const trapSpacesForRequiredFields = (value: string) =>
  !!value.trim() || 'This field is required';

type FormInputs = {
  id?: string;
  title: string;
  payableAccountId: string | null;
  expenseAccountId: string | null;
  behavior: taxBehavior_enum | null;
  countryCode: string | null;
  rateSuperBasePoints: number | null;
};

type Props = {
  opened: boolean;
  closeModal: () => void;
  taxRate?: (FormInputs & { status: activeStatus_enum }) | null;
  zIndex?: number;
};

export const TaxRateModal = ({
  opened,
  closeModal: cl,
  taxRate,
  zIndex,
}: Props) => {
  const invalidate = useInvalidateHasuraQueries(['taxRates']);

  const { mutateAsync: insert } = useApiMutation('post', '/rates');
  const { mutateAsync: update } = useApiMutation('put', '/rates/{id}');

  const methods = useForm<FormInputs>({
    values: taxRate
      ? {
          payableAccountId: taxRate.payableAccountId,
          expenseAccountId: taxRate.expenseAccountId,
          rateSuperBasePoints: taxRate.rateSuperBasePoints ?? 0,
          title: taxRate.title,
          behavior: taxRate.behavior,
          countryCode: taxRate.countryCode,
        }
      : {
          rateSuperBasePoints: null,
          title: '',
          behavior: 'included',
          countryCode: null,
          payableAccountId: null,
          expenseAccountId: null,
        },
  });

  const submit = async (data: FormInputs) => {
    try {
      const basisPoints = data.rateSuperBasePoints ?? 0;
      const payableAccountId = data.payableAccountId ?? null;

      if (!taxRate?.id) {
        await insert({
          body: {
            name: data.title,
            basisPoints,
            accountId: payableAccountId,
            type: 'tax',
            status: 'active',
            countryCode: data.countryCode,
            debitAccountId: data.expenseAccountId,
          },
        });
      } else {
        await update({
          params: {
            path: {
              id: taxRate.id,
            },
          },
          body: {
            basisPoints,
            accountId: payableAccountId,
            name: data.title,
            type: 'tax',
            status: taxRate.status,
            countryCode: data.countryCode,
            debitAccountId: data.expenseAccountId,
          },
        });
      }

      showSuccessNotification({
        title: 'Success!',
        message: `Tax rate was ${taxRate ? 'updated' : 'added'} successfully`,
      });

      invalidate();
      closeModal();
    } catch (error: any) {
      const errorMessage =
        error?.message ||
        error.issues?.[0]?.message ||
        'Please try again later and if the problem persists, contact support.';

      showErrorNotification({
        message: errorMessage,
        title: 'Failed to save tax rate',
      });
    }
  };

  const closeModal = () => {
    cl();
    methods.reset({
      rateSuperBasePoints: null,
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={closeModal}
      title={`${taxRate ? 'Update' : 'Add'} Tax Rate`}
      styles={{
        title: { fontWeight: 500 },
      }}
      zIndex={zIndex}
      centered
    >
      <FormProvider {...methods}>
        <Box
          sx={(theme) => ({
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing.md,
          })}
        >
          <Controller
            control={methods.control}
            rules={{
              required: 'Name is required',
              validate: trapSpacesForRequiredFields,
            }}
            name="title"
            render={({ field, fieldState }) => (
              <InputWrapper
                required
                label="Name"
                error={fieldState.error?.message}
              >
                <Input
                  value={field.value}
                  setValue={field.onChange}
                  error={!!fieldState.error}
                  placeholder="Sales tax (10%)"
                />
              </InputWrapper>
            )}
          />

          <Controller
            control={methods.control}
            rules={{
              validate: (value) => {
                if (typeof value !== 'number') {
                  return 'Please enter a number';
                }

                if (value <= 0) {
                  return 'Please enter a positive rate';
                }
              },
            }}
            name="rateSuperBasePoints"
            render={({ field, fieldState }) => (
              <InputWrapper
                required
                label="Rate"
                error={fieldState.error?.message}
              >
                <InputPercentage
                  value={
                    typeof field.value === 'number' ? field.value / 1000 : ''
                  }
                  onChange={(value) => {
                    const v =
                      value === '' ? null : bankersRound(Number(value) * 1000);

                    field.onChange(v);
                  }}
                  error={!!fieldState.error}
                  placeholder="10"
                  min={0}
                  max={100}
                  decimalScale={3}
                />
              </InputWrapper>
            )}
          />

          <AccountSelect name="payableAccountId" />
          <AccountSelect name="expenseAccountId" />

          <Controller
            control={methods.control}
            name="countryCode"
            render={({ field, fieldState }) => (
              <InputWrapper label="Country" error={fieldState.error?.message}>
                <InputCountry
                  value={field.value ?? null}
                  setValue={(v) => field.onChange(v?.value ?? null)}
                  error={!!fieldState.error}
                  placeholder="United States of America"
                  clearable
                />
              </InputWrapper>
            )}
          />

          {/* <Controller
          control={methods.control}
          name={'behavior'}
          render={({ field }) => {
            return (
              <Checkbox
                checked={field.value === 'included'}
                label="Include tax by default when applying to amounts"
                onChange={(event) =>
                  field.onChange(
                    event.currentTarget.checked ? 'included' : 'excluded'
                  )
                }
                size="xs"
                sx={(theme) => ({
                  flex: 1,
                  color: theme.colors.neutral[8],
                })}
              />
            );
          }}
        /> */}

          <Group justify="right" mt="md">
            <Button
              onClick={closeModal}
              disabled={methods.formState.isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={methods.handleSubmit(submit, (err) =>
                console.error(err)
              )}
              loading={methods.formState.isSubmitting}
            >
              {taxRate ? 'Update' : 'Add tax rate'}
            </Button>
          </Group>
        </Box>
      </FormProvider>
    </Modal>
  );
};

function useAccountQuery({
  restrictToIncomeStatementAccounts,
  search,
}: {
  restrictToIncomeStatementAccounts: boolean;
  search: string;
}) {
  const [{ id: teamId }] = useTeam();

  return useInfiniteQuery(
    (
      q,
      { restrictToIncomeStatementAccounts, teamId, search },
      { limit, offset }
    ) => {
      const where: account_bool_exp = {
        ...whereAccounts({
          tenantId: teamId,
          search,
        }),
        category: restrictToIncomeStatementAccounts
          ? {
              classification: { _in: ['expense', 'revenue'] },
            }
          : undefined,
        type: { _eq: 'ledger' },
      };

      const list = q
        .accounts({
          where,
          order_by: defaultAccountOrderBy,
          limit,
          offset,
        })
        .map<SelectItem>((account) => {
          const assignments = account
            .assignments({
              where: {
                type: { _in: ['accountsPayable', 'accountsReceivable'] },
              },
            })
            .map((a) => formatAssignmentType(a.type));

          const disabled = !!assignments.length;

          const disabledDescription =
            assignments.length === 1
              ? `This account is assigned to ${assignments[0]}`
              : `This account is assigned to ${assignments.join(' & ')}`;

          return {
            label: account.title || 'No name',
            value: account.id,
            group: toTitleCase(
              account.category?.classification || 'No classification'
            ),
            disabled,
            description: disabled ? disabledDescription : undefined,
          };
        });

      const aggregate = q.accountAggregate({ where }).aggregate?.count() || 0;

      return {
        list,
        aggregate,
      };
    },
    {
      skip: !teamId,
      queryKey: ['accounts'],
      variables: {
        restrictToIncomeStatementAccounts,
        teamId,
        search: search?.trim(),
      },
    }
  );
}

type AccountType = 'payableAccountId' | 'expenseAccountId';

const AccountSelect = ({ name }: { name: AccountType }) => {
  const methods = useFormContext<FormInputs>();

  const defaultAccountQueryData = useTaxRateAccountAssignmentQuery();

  const [search, setSearch] = useState('');
  const accountQueryData = useAccountQuery({
    search,
    restrictToIncomeStatementAccounts: name === 'expenseAccountId',
  });

  const payableAccountId = methods.watch('payableAccountId');

  return (
    <Controller
      control={methods.control}
      name={name}
      render={({ field, fieldState }) => {
        const { data, isLoading: loading } = useQuery(
          (q, args) => {
            return {
              account: !args.value
                ? null
                : q
                    .accounts({
                      where: {
                        id: { _eq: args.value },
                      },
                    })
                    .map<SelectItem>((item) => ({
                      label: item.title || 'No name',
                      value: item.id!,
                    }))[0],
              payableAccount: !args.payableAccountId
                ? null
                : q
                    .accounts({
                      where: {
                        id: { _eq: args.payableAccountId },
                      },
                    })
                    .map<SelectItem>((item) => ({
                      label: item.title || 'No name',
                      value: item.id!,
                    }))[0],
            };
          },
          {
            keepPreviousData: true,
            variables: {
              value: field.value,
              payableAccountId,
            },
          }
        );

        const account = field.value ? data?.account || null : null;

        const payablePlaceholder = defaultAccountQueryData.data?.title
          ? `Default: ${defaultAccountQueryData.data.title}`
          : 'Select account';

        const expensePlaceholder = 'Select account';

        return (
          <InputWrapper
            error={fieldState.error?.message}
            label={
              name === 'payableAccountId' ? (
                'Payable Account'
              ) : (
                <Group gap="xs">
                  <Text fw={500} span>
                    Expense Account
                  </Text>
                  <Tooltip
                    label="Use the expense account to override the account used when applying the tax rate to one of your fees or commissions."
                    multiline
                    maw={300}
                    withArrow
                    inline
                  >
                    <Icon display="inline" icon="InfoIcon" size={16} />
                  </Tooltip>
                </Group>
              )
            }
          >
            <InputSelect
              type="single"
              value={account}
              setValue={(v) => {
                const newAccountId = v?.value || null;

                field.onChange(newAccountId);

                if (name === 'payableAccountId' && !newAccountId)
                  methods.setValue('expenseAccountId', null);
              }}
              infiniteData={{ ...accountQueryData, setSearch }}
              inputProps={{
                error: !!fieldState.error,
                placeholder:
                  name === 'payableAccountId'
                    ? payablePlaceholder
                    : expensePlaceholder,
                loadingQuery: loading || defaultAccountQueryData.isLoading,
                withClearButton: true,
              }}
              dropdownProps={{
                width: 'target',
              }}
            />
          </InputWrapper>
        );
      }}
    />
  );
};
